/**
 * A worked example behind every capability card. Modelled on Trace's (t12) and t4's.
 *
 * EVERY CARD HAS ONE, BUILT OR NOT, as on Trace's page: nothing in eTMF is built yet, so an example of a card that is not
 * Available is drawn under a banner saying it describes how the feature will work, and the guard test checks the page
 * still carries that banner.
 *
 * ONE RUNNING CASE. Every example uses the same study -- INF-301, the pivotal study of an infusion pump, sponsored by
 * an invented device company at twelve sites in the United States and Canada, numbered 101 to 112 -- so the pages read
 * as one story a buyer can follow. The company, the sites and the people are invented.
 */

export type Step = {
    /** What the person does. */
    does: string;
    /** What happens, in terms they can check against their own screen. */
    then: string;
};

export type Sample = {
    caption: string;
    /** `json` and `text` are monospaced; `quote` is the product's own wording, rendered as prose. */
    kind: "json" | "text" | "quote";
    body: string;
};

export type CapabilityExample = {
    slug: string;
    /** The situation, in one sentence. */
    scenario: string;
    steps: Step[];
    samples?: Sample[];
    /** The thing most people get wrong about this capability. Every example has one. */
    trap: {heading: string; body: string};
    /** The same thing from a pipeline, where there is an endpoint for it. */
    api?: string;
    /** A guide that goes further, if one exists. */
    guide?: {file: string; label: string};
};

export const EXAMPLES: CapabilityExample[] = [
    // ── Studies and their structure ─────────────────────────────────────────────────────────
    {
        slug: "study-setup",
        scenario: "The clinical team is starting INF-301 at ten sites in the United States and two in Canada.",
        steps: [
            {does: "Create the study with its protocol number, phase and device, add the two countries, and add sites 101 to 112 with their principal investigators.",
             then: "Each country and site has its folders and its list of expected documents, and every site starts in Selected."},
            {does: "Add site 113 in month four.",
             then: "Site 113 gets its folders and expected documents the moment it is added; nothing else in the study changes."},
        ],
        trap: {
            heading: "Closing a site is not deleting it",
            body: "A site that withdrew still has documents the regulations require the sponsor to keep. Closing it makes its file read-only; it stays in the study's file and in its export.",
        },
    },
    {
        slug: "reference-model",
        scenario: "INF-301's quality lead wants the file organised the way an inspector expects to read it.",
        steps: [
            {does: "Open the study's file.",
             then: "It is laid out in the TMF Reference Model's zones and sections, at study, country and site level, with the model's numbering beside each artifact."},
            {does: "Open site 104's folder.",
             then: "Only the site-level artifacts appear there: the investigator's CV, the delegation log, the ethics approval, the monitoring visit reports."},
        ],
        trap: {
            heading: "The model is a vocabulary, not a filing cabinet",
            body: "Two people can file the same document under different artifacts. The model makes the place predictable only if the organisation's template says which artifact each kind of document is, and quality review checks it.",
        },
    },
    {
        slug: "study-templates",
        scenario: "The organisation runs device studies, which need artifacts a drug study never has, such as device accountability.",
        steps: [
            {does: "Add device accountability and the device's instructions for use to the device-study template, and hide the drug-only artifacts.",
             then: "Every new device study starts with them; studies already running are listed as ones the change would affect, and are left alone."},
            {does: "Apply the change to INF-301 on purpose.",
             then: "Its sites gain placeholders for device accountability, and the change is recorded on the study."},
        ],
        trap: {
            heading: "A template change does not rewrite running studies",
            body: "Changing what a running study expects changes its completeness overnight. That is applied study by study, by a person, never as a side effect of editing the template.",
        },
    },
    {
        slug: "milestones",
        scenario: "Site 107 has been selected but not yet initiated.",
        steps: [
            {does: "Look at site 107's completeness.",
             then: "It counts only what is due by site initiation -- the CV, the signed agreement, the ethics approval -- not the visit reports that come later."},
            {does: "Date site 107's initiation visit.",
             then: "The documents expected by initiation are now due by that date, and their owners see them on their lists."},
        ],
        trap: {
            heading: "A file judged against everything is always behind",
            body: "Without milestones, every new site is mostly incomplete on its first day, and a number that is always red gets ignored. Expectations follow the study's progress.",
        },
    },

    // ── Knowing what is missing ─────────────────────────────────────────────────────────────
    {
        slug: "expected-documents",
        scenario: "The study coordinator needs to know what site 104 still owes before its initiation visit.",
        steps: [
            {does: "Open site 104's expected documents.",
             then: "Each placeholder shows the artifact, who owes it and the due date: the investigator's CV and licence are filed; the laboratory's accreditation is still missing."},
            {does: "Mark the laboratory accreditation not applicable, because site 104 uses the central laboratory.",
             then: "Trace asks for the reason, keeps it on the placeholder, and site 104's completeness rises."},
        ],
        trap: {
            heading: "Not applicable is a decision someone made",
            body: "A placeholder dismissed without a reason is the first thing an inspector asks about. The reason, who gave it and when stay on the placeholder for as long as the study is kept.",
        },
    },
    {
        slug: "completeness",
        scenario: "The study manager reports progress to the steering committee every month.",
        steps: [
            {does: "Open INF-301's completeness.",
             then: "The study stands at 86%, Canada at 79% and site 111 at 61%; each number opens the list of documents it is missing."},
            {does: "Open site 111's list.",
             then: "Seven documents: four CVs of new sub-investigators, two training records and an updated ethics approval."},
        ],
        trap: {
            heading: "Filed is not final",
            body: "A document waiting for quality review is in the file but does not count. Completeness counts final documents only, so a backlog of reviews shows as a gap, which it is.",
        },
    },
    {
        slug: "timeliness",
        scenario: "The organisation's target is that a document is final in the file within thirty days of its date.",
        steps: [
            {does: "Open timeliness for INF-301.",
             then: "Monitoring visit reports average 41 days at site 106 against 18 days at the other sites."},
            {does: "Open site 106's late reports.",
             then: "Each is listed with its monitor, so the cause is a conversation with a named person rather than an average."},
        ],
        trap: {
            heading: "Late documents look fine once they arrive",
            body: "A complete file can still have been filed months late, and inspectors look at when, not just whether. Timeliness is measured from the document's own date, not the upload date.",
        },
    },

    // ── Filing ──────────────────────────────────────────────────────────────────────────────
    {
        slug: "upload-and-bulk",
        scenario: "After a monitoring visit at site 102, the monitor has 38 scanned documents to file.",
        steps: [
            {does: "Drop all 38 into INF-301's inbox.",
             then: "Each arrives with its fingerprint recorded, waiting to be filed."},
            {does: "Select the twelve training records, choose the artifact and site, and file them together.",
             then: "Each fills its placeholder and goes to quality review."},
        ],
        trap: {
            heading: "A scan of the wrong page is still a file",
            body: "Bulk upload makes filing fast, and makes filing the wrong thing fast. Quality review exists for this: a blank, duplicated or sideways page is returned before it counts.",
        },
    },
    {
        slug: "file-by-email",
        scenario: "Site 109's coordinator has no account and is used to emailing documents to the sponsor.",
        steps: [
            {does: "Site 109 emails its renewed ethics approval to INF-301's address.",
             then: "It arrives in the study's inbox with the sender, the subject and the time, waiting to be filed."},
            {does: "A message arrives from an address the study does not know.",
             then: "It is held for a person to accept or reject; it is never filed automatically."},
        ],
        trap: {
            heading: "An email address is not an identity",
            body: "Anybody can send to the address. Filing by email gets documents in; it does not make the sender a signer, and nothing arriving this way is final until it has been reviewed.",
        },
    },
    {
        slug: "classification",
        scenario: "The inbox holds a PDF named scan_0412.pdf.",
        steps: [
            {does: "Open it in the inbox.",
             then: "It is offered as the curriculum vitae of a sub-investigator at site 105, dated 12 March, with the reason: the heading, the name and the signature date read from the page."},
            {does: "Correct the site to 113, and file it.",
             then: "It is filed at site 113, and the record says who confirmed the classification and that the suggestion was corrected."},
        ],
        trap: {
            heading: "A suggestion is not a filing",
            body: "A classifier that is right nine times in ten misfiles one document in ten without anybody noticing. Every suggestion is confirmed by a person, and the record says who.",
        },
    },

    // ── Controlled documents ────────────────────────────────────────────────────────────────
    {
        slug: "document-lifecycle",
        scenario: "A monitoring visit report for site 103 has been filed and needs to count.",
        steps: [
            {does: "The reviewer opens it from their review list and finds the monitor's signature missing.",
             then: "They return it with the reason Signature missing; the monitor sees it on their list with the reason."},
            {does: "The monitor files the signed version, and the reviewer passes it.",
             then: "It is final, fills its placeholder, and counts towards site 103's completeness."},
        ],
        trap: {
            heading: "Review is not approval",
            body: "Quality review checks that the document is the right one, legible, complete and filed in the right place. Whether the visit's findings are acceptable is a different judgement, made in the document, by the people who signed it.",
        },
    },
    {
        slug: "versions",
        scenario: "INF-301's protocol is amended to version 3.0.",
        steps: [
            {does: "File version 3.0 over version 2.0.",
             then: "Version 3.0 is current; version 2.0 stays readable, marked superseded, with the date it was replaced."},
            {does: "Open the protocol's history.",
             then: "Every version, each with its review, its signatures and the dates it was in force."},
        ],
        trap: {
            heading: "The old version still matters",
            body: "Participants consented under version 2.0 were treated under it. A superseded document is kept and readable for as long as the study is, never replaced in place.",
        },
    },
    {
        slug: "signatures",
        scenario: "The investigator's brochure needs the medical monitor's approval.",
        steps: [
            {does: "Request the medical monitor's signature, due Friday.",
             then: "It appears on their list of signatures waiting."},
            {does: "The medical monitor signs with their password, meaning Approved.",
             then: "The signature records their name, the time, the meaning and the version signed."},
        ],
        trap: {
            heading: "A signature belongs to one version",
            body: "Filing a new version does not carry the signature forward. The new version needs its own, because the person signed what they read.",
        },
    },
    {
        slug: "expiry-and-due",
        scenario: "The principal investigator at site 110 has a medical licence that expires on 30 June.",
        steps: [
            {does: "File the licence with its expiry date.",
             then: "Thirty days before it lapses, the site coordinator is told, and the licence appears as expiring on the dashboard."},
            {does: "Let 30 June pass without a renewal.",
             then: "The licence is listed as expired, and its placeholder reopens until the renewal is filed."},
        ],
        trap: {
            heading: "An expired document is still in the file",
            body: "The old licence was valid for the period it covered and stays filed. Expiry reopens the placeholder for its successor; it does not remove the evidence of the period it covered.",
        },
    },
    {
        slug: "redaction",
        scenario: "A source document from site 108 shows a participant's name and date of birth.",
        steps: [
            {does: "Mark it as holding personal data and file a redacted copy beside it.",
             then: "A monitor without the right to see personal data opens the redacted copy only."},
            {does: "A person with that right opens the original.",
             then: "The opening is recorded on its audit trail with who, when and from where."},
        ],
        trap: {
            heading: "Hiding is not redacting",
            body: "Blacking out text in a viewer leaves it in the file underneath. The redacted copy is a separate document with the data removed, and that is what people without the right are given.",
        },
    },

    // ── Sites ───────────────────────────────────────────────────────────────────────────────
    {
        slug: "site-files",
        scenario: "Site 101 keeps its investigator site file in the product.",
        steps: [
            {does: "Site 101's coordinator files the site's own correspondence in the site file.",
             then: "The sponsor does not see it: it belongs to the site."},
            {does: "The coordinator shares the signed delegation log with the sponsor.",
             then: "The sponsor sees that log, and only that, in its copy of site 101's records."},
        ],
        trap: {
            heading: "One shared folder is not two files",
            body: "The regulations expect the sponsor and the investigator each to keep their own records. A single folder both sides edit leaves neither able to say what it held; sharing is explicit and recorded.",
        },
    },
    {
        slug: "site-exchange",
        scenario: "Protocol version 3.0 must reach all twelve sites.",
        steps: [
            {does: "Send version 3.0 to all sites, asking for acknowledgement.",
             then: "Ten sites acknowledge within the week; sites 106 and 112 are listed as received, not acknowledged."},
            {does: "Site 106 sends back its ethics approval of the amendment.",
             then: "It arrives in the trial master file's inbox for review, with the transfer recorded on both sides."},
        ],
        trap: {
            heading: "Sent is not received",
            body: "An email can sit unread for weeks. The sponsor sees acknowledgement per site, so a site still working to the old protocol is visible on the day it matters.",
        },
    },
    {
        slug: "delegation-log",
        scenario: "Site 105 adds a study nurse who will take consent and record device readings.",
        steps: [
            {does: "Add a row for the nurse with those two tasks from 1 May, signed by the nurse and by the principal investigator.",
             then: "The log shows the nurse may take consent and record readings from 1 May."},
            {does: "Remove consent from the nurse's tasks on 1 August.",
             then: "The row gets a new version; the log still shows the nurse could take consent between 1 May and 31 July."},
        ],
        trap: {
            heading: "A delegation log is a history",
            body: "Inspectors ask who was allowed to do a task on the day it was done. Editing a row in place loses the answer; every change makes a new version and the old one stays.",
        },
    },
    {
        slug: "site-logs",
        scenario: "Every site keeps a device accountability log of each pump received, used and returned.",
        steps: [
            {does: "Build the log template once, with columns for serial number, date received, participant, date returned and a signature.",
             then: "All twelve sites keep the same log with the same columns."},
            {does: "Site 102 records a pump returned and signs the row.",
             then: "The row is versioned and signed, and the sponsor sees the site's count of pumps on hand."},
        ],
        trap: {
            heading: "A spreadsheet is not a log",
            body: "A spreadsheet uploaded weekly cannot say who changed a row or when. A log kept in the product records each row's versions and signatures, which is what makes it evidence.",
        },
    },
    {
        slug: "site-training",
        scenario: "Protocol version 3.0 changes how occlusion alarms are handled at the bedside.",
        steps: [
            {does: "File version 3.0.",
             then: "Every person on a delegation log with a clinical task is due training on version 3.0."},
            {does: "Site 104's study nurse records a device reading before being trained.",
             then: "The delegation log flags the nurse as delegated a task without current training."},
        ],
        trap: {
            heading: "Trained once is not trained now",
            body: "Training on version 2.0 does not cover version 3.0. Training is recorded against a version, and a new version reopens it for the people it affects.",
        },
    },

    // ── Access and inspection ───────────────────────────────────────────────────────────────
    {
        slug: "folder-permissions",
        scenario: "Contracts and budgets sit in the study's file beside the clinical documents, and only the contracts team may see them.",
        steps: [
            {does: "Give the contracts folder to the contracts team only.",
             then: "Everybody else sees the study's file without that folder in it, not even its name."},
            {does: "Give site 104's coordinator file permission on site 104's folders.",
             then: "The coordinator can file there and nowhere else, and cannot see the other sites."},
        ],
        trap: {
            heading: "A greyed-out folder still tells a story",
            body: "Showing a locked folder's name tells everybody it exists, and sometimes the name is the confidential part. A folder a person may not see is not shown at all.",
        },
    },
    {
        slug: "outside-access",
        scenario: "A CRO monitor covers sites 101 to 104 for the first six months.",
        steps: [
            {does: "Give the monitor read access to those four sites' folders until 31 December.",
             then: "On 1 January the access ends by itself."},
            {does: "Run the access report for the study.",
             then: "It lists the monitor, the four sites, and the dates access began and ended."},
        ],
        trap: {
            heading: "Access nobody removes is access forever",
            body: "Most leftover access comes from people who moved on and were never removed. Outside access always has an end date, so forgetting is safe.",
        },
    },
    {
        slug: "inspector-view",
        scenario: "An FDA inspector arrives to inspect INF-301.",
        steps: [
            {does: "Give the inspector the study's final documents, read-only, for the week.",
             then: "They see the file on the model, with no drafts, no inbox and no folders they cannot open."},
            {does: "At the end of the day, look at what the inspector opened.",
             then: "Every document they opened, in order, with the time."},
        ],
        trap: {
            heading: "An inspector should not see drafts",
            body: "A draft is not part of the record and can be misread as one. The inspector's view holds final documents only, by design rather than by somebody remembering to hide drafts.",
        },
    },
    {
        slug: "audit-trail",
        scenario: "An auditor asks how site 103's monitoring visit report reached the file.",
        steps: [
            {does: "Open the report's audit trail.",
             then: "Filed by the monitor on 3 April, returned by the reviewer for a missing signature, filed again signed on 5 April, passed on 6 April, opened by the auditor today."},
            {does: "Export the report.",
             then: "Its audit trail is exported with it."},
        ],
        trap: {
            heading: "Opening is an act too",
            body: "Most products record changes and not reads. For a document holding personal data, who opened it is the question, so openings and downloads are recorded as well.",
        },
    },
    {
        slug: "legal-hold",
        scenario: "INF-301 has closed and the organisation keeps trial files for fifteen years.",
        steps: [
            {does: "Close the study.",
             then: "Its file is locked: nothing can be filed, changed or removed, and the earliest date it may be destroyed is set fifteen years ahead."},
            {does: "Try to remove a document from the closed study.",
             then: "It is refused, and the refusal says the study is closed."},
        ],
        trap: {
            heading: "Read-only is not retained",
            body: "A file nobody can edit can still be deleted with the account. Retention sets when it may be destroyed and who must approve that, so the file outlives anybody's tidying up.",
        },
    },

    // ── Inspection readiness ────────────────────────────────────────────────────────────────
    {
        slug: "coordinator-dashboard",
        scenario: "Site 104's coordinator starts the week by checking what needs doing.",
        steps: [
            {does: "Open site 104's dashboard.",
             then: "Three documents missing, one expired, two expiring within thirty days, four waiting for review, one returned and two waiting for a signature."},
            {does: "Choose Expiring.",
             then: "The principal investigator's GCP certificate and the laboratory's accreditation, each opening where the renewal is filed."},
        ],
        trap: {
            heading: "A number nobody can open is decoration",
            body: "A dashboard that says 4 without saying which four sends people searching. Every number opens its list, and every line opens the place the work is done.",
        },
    },
    {
        slug: "my-work",
        scenario: "The study's quality reviewer works across three studies.",
        steps: [
            {does: "Open My work.",
             then: "Fourteen reviews across the three studies, two signatures waiting, and one document of theirs returned."},
            {does: "Open the first review.",
             then: "The document opens in quality review, where it is passed or returned."},
        ],
        trap: {
            heading: "Work spread across studies is work nobody sees",
            body: "A reviewer who must open each study to find their work misses some of it. My work gathers it across every study they can see.",
        },
    },
    {
        slug: "readiness-report",
        scenario: "The sponsor expects an inspection of INF-301 next month.",
        steps: [
            {does: "Produce the readiness report as of today.",
             then: "Completeness and timeliness by country and site, the documents missing or expired, review findings by kind, and who had access."},
            {does: "Produce it again in two weeks.",
             then: "The two reports, side by side, show what was fixed in between."},
        ],
        trap: {
            heading: "A report is a record of a date",
            body: "A report regenerated later shows a different file. The readiness report says the date it was produced as of and is kept, so it can be shown as what the sponsor knew then.",
        },
    },

    // ── Export and exchange ─────────────────────────────────────────────────────────────────
    {
        slug: "index-export",
        scenario: "The sponsor's regulatory team wants a copy of INF-301's file outside the product.",
        steps: [
            {does: "Export the study's file.",
             then: "A folder of documents on the model, with an index listing each document's artifact, site, version, dates, state and signatures, and each document's audit trail."},
            {does: "Open it on a computer with nothing installed.",
             then: "Everything reads: the index is a spreadsheet and the documents are PDFs."},
        ],
        trap: {
            heading: "Documents without an index are a pile",
            body: "An export of files alone loses what made the file a file: which artifact each is, which version, whether it was final. The index carries that.",
        },
    },
    {
        slug: "tmf-exchange",
        scenario: "The sponsor hands INF-301's follow-up phase to a CRO that runs its own eTMF.",
        steps: [
            {does: "Export the file in the Reference Model's exchange format.",
             then: "The CRO's system imports it with the artifacts, sites, versions and dates intact."},
            {does: "The CRO returns the file at the end of follow-up.",
             then: "It is imported back into INF-301, and each document's origin is recorded."},
        ],
        trap: {
            heading: "A transfer is where files lose their metadata",
            body: "Moving a study between systems by zip file turns a structured file into a pile. The exchange format keeps the structure, which is the part that is hardest to rebuild.",
        },
    },
    {
        slug: "archive",
        scenario: "INF-301 has closed.",
        steps: [
            {does: "Produce the archive.",
             then: "The final file, its index, every audit trail and a fingerprint of every document, with a page saying what it was produced from and when."},
            {does: "Check a document from the archive years later.",
             then: "Its fingerprint matches the one in the index, so it is the document that was archived."},
        ],
        trap: {
            heading: "An archive must prove it was not changed",
            body: "A copy anybody could have edited is not evidence. The fingerprints in the index let anybody check, years later, that a document is the one archived.",
        },
    },

    // ── On its own or with the QMS ──────────────────────────────────────────────────────────
    {
        slug: "standalone",
        scenario: "A sponsor running its first study has no quality system software yet.",
        steps: [
            {does: "The sponsor's people sign into eTMF.",
             then: "Studies, files, review, signatures, the audit trail and access are all on eTMF's own screens."},
            {does: "The sponsor's auditor asks for the signature on a document.",
             then: "It is on the document in eTMF, with the name, time, meaning and version."},
        ],
        trap: {
            heading: "On its own means nothing borrowed",
            body: "A product that needs another product's screens for its controls is not sold on its own. Every control a sponsor needs for its trial file is on eTMF's screens.",
        },
    },
    {
        slug: "with-the-qms",
        scenario: "The sponsor also runs the QMS, where its SOPs live.",
        steps: [
            {does: "Cite the monitoring SOP in INF-301's file.",
             then: "The citation points at the SOP in document control, so the file always shows the version in force."},
            {does: "A protocol deviation at site 106 needs corrective action.",
             then: "It is raised as a CAPA in the QMS, with the deviation's documents cited, without an integration between two products."},
        ],
        trap: {
            heading: "A copy of an SOP goes stale",
            body: "An SOP copied into the trial file is out of date the day it is revised. Citing it from document control keeps one copy, the controlled one.",
        },
    },
    {
        slug: "tenant-profiles",
        scenario: "A new customer buys eTMF only.",
        steps: [
            {does: "The account is created for eTMF.",
             then: "It has eTMF's roles, a study template and review rules, and none of the QMS's document types, matrices or training."},
            {does: "A year later the customer adds the QMS.",
             then: "The QMS is added alongside; nothing in eTMF is migrated or changed."},
        ],
        trap: {
            heading: "A customer should not see what it did not buy",
            body: "An eTMF-only account full of the QMS's setup confuses its people and its auditors. The account is set up for what was bought.",
        },
    },

    // ── Search and the assistant ────────────────────────────────────────────────────────────
    {
        slug: "search",
        scenario: "The study manager needs every CV signed before 2025 across all sites.",
        steps: [
            {does: "Search for artifact CV, signed before 1 January 2025, and save the search.",
             then: "Nine CVs across six sites, each opening its document."},
            {does: "Reload the page.",
             then: "The search and its settings are still there."},
        ],
        trap: {
            heading: "Search that forgets is search people stop using",
            body: "Losing the criteria on a reload sends people back to browsing folders. Searches keep their settings, and can be saved.",
        },
    },
    {
        slug: "assistant",
        scenario: "The study manager asks which sites cannot consent participants this week.",
        steps: [
            {does: "Ask: which sites have nobody currently delegated and trained to take consent?",
             then: "Site 112, with the delegation log and the training records it read cited."},
            {does: "Ask the same question as a monitor who covers sites 101 to 104.",
             then: "The answer covers those four sites only, because it reads only what the asker may see."},
        ],
        trap: {
            heading: "An answer without a source is not an answer",
            body: "An assistant that says site 112 without showing why cannot be checked. Every answer cites the documents it read.",
        },
    },
];

export const EXAMPLE_BY_SLUG: Record<string, CapabilityExample> = Object.fromEntries(
    EXAMPLES.map(one => [one.slug, one]));
